// Archiving a property with costs that are still to settle. Once archived, a
// property's own costs can only be settled after it is activated again, and a
// unit keeps its part in each open cost of its group that began before it was
// archived, for that cost's whole period (see groupUnits.utils). Archiving
// stays possible: the broker is told first.

import * as propertyCostApi from "@rentiohq/shared-frontend/dist/reduxV2/propertyCost/propertyCost.api";
import * as settlementApi from "@rentiohq/shared-frontend/dist/reduxV2/settlement/settlement.api";
import {
  costSettlementStateOf,
  costSettlementStates,
} from "./costSettlementState.utils";
import { existedSince } from "./groupUnits.utils";
import { todayIsoDay } from "./localDay.utils";
import { fromCents, toCents, vatOfAmount } from "./money.utils";
import { propertyCostToInvoice } from "./propertyCost.mapper";
import { settlementFromApi } from "./settlement.mapper";
import { Invoice, Settlement, settlementEligible, vatPercent } from "./types";

// The costs a settlement could still take: eligible, and in no confirmed
// settlement yet. A departure settlement leaves the rest of its costs open.
export const openCosts = (params: {
  costs: Invoice[];
  settlements: Settlement[];
}): Invoice[] => {
  const states = costSettlementStates(params.settlements);
  return params.costs.filter(
    cost =>
      settlementEligible(cost) &&
      !costSettlementStateOf(states, cost.id).settledIn,
  );
};

export interface IOpenCostsBeforeArchive {
  // Open costs of the property itself.
  own: number;
  // Open costs of its group that it keeps a share in, when it is a unit of
  // one.
  group: number;
}

// What archiving a property on `archiveDay` leaves open: its own costs still
// to settle, and those of its group it keeps its share in. A unit takes part
// in every group cost that began on or before the day it is archived, for
// that cost's whole period (see groupUnits.utils existedSince); a group cost
// that begins later is split without it.
export const openCostsBeforeArchive = (params: {
  costs: Invoice[];
  settlements: Settlement[];
  propertyId: number;
  parentId?: number;
  archiveDay: string;
}): IOpenCostsBeforeArchive => {
  const { costs, settlements, propertyId, parentId, archiveDay } = params;
  const open = openCosts({ costs, settlements });
  const keepsShareIn = (cost: Invoice) =>
    existedSince({ archivedOn: archiveDay, from: cost.periodFrom });
  return {
    own: open.filter(cost => cost.unitId === String(propertyId)).length,
    group:
      parentId != null
        ? open.filter(
            cost => cost.unitId === String(parentId) && keepsShareIn(cost),
          ).length
        : 0,
  };
};

const PAGE_LIMIT = 100;

const propertyFilter = (ids: number[]) =>
  ids.length === 1
    ? { propertyId: { eq: ids[0] } }
    : { or: ids.map(id => ({ propertyId: { eq: id } })) };

// Every page of a list, one request after the other.
const fetchEveryPage = async <TItem>(
  fetchPage: (page: number) => Promise<{ items: TItem[]; totalPages: number }>,
): Promise<TItem[]> => {
  const items: TItem[] = [];
  let page = 1;
  let totalPages = 1;
  do {
    const result = await fetchPage(page);
    items.push(...result.items);
    ({ totalPages } = result);
    page += 1;
  } while (page <= totalPages);
  return items;
};

// The open costs of a property and of its group, with the settlements that
// may already hold them.
export const fetchOpenCostsBeforeArchive = async (params: {
  propertyId: number;
  parentId?: number;
}): Promise<IOpenCostsBeforeArchive> => {
  const { propertyId, parentId } = params;
  const ids = parentId != null ? [propertyId, parentId] : [propertyId];
  const filter = propertyFilter(ids);
  const [costs, settlements] = await Promise.all([
    fetchEveryPage(async page => {
      const response = await propertyCostApi.getPaged({
        query: { page, limit: PAGE_LIMIT, filter },
      });
      const { items, meta } = response.data.data;
      return {
        items: items.map(propertyCostToInvoice),
        totalPages: meta.totalPages,
      };
    }),
    fetchEveryPage(async page => {
      const response = await settlementApi.getPaged({
        query: { page, limit: PAGE_LIMIT, filter },
      });
      const { items, meta } = response.data.data;
      return {
        items: items.map(settlementFromApi),
        totalPages: meta.totalPages,
      };
    }),
  ]);
  return openCostsBeforeArchive({
    costs,
    settlements,
    propertyId,
    parentId,
    archiveDay: todayIsoDay(),
  });
};

// What taking a unit out of its building means for the building's costs
// (decision 5 of 2026-10-20). A unit out of the building takes part in none
// of its costs any more, also those that began before: its share moves to
// the other units. Archiving keeps it (see openCostsBeforeArchive).
export interface IGroupLeave {
  // The building's confirmed settlements that name the unit: it cannot
  // leave then, their statements and shares rest on it.
  namedIn: Settlement[];
  // The building's costs still to settle, and their total incl. VAT.
  openCosts: number;
  openTotal: number;
}

// Whether a confirmed settlement counted this unit: among its units, or
// among the units of its tenants.
const namesUnit = (settlement: Settlement, unitId: string): boolean =>
  settlement.status === "confirmed" &&
  (!!settlement.snapshot?.groupUnits?.some(
    unit => String(unit.id) === unitId,
  ) ||
    !!settlement.snapshot?.perContract?.some(
      row => String(row.unitId) === unitId,
    ));

export const groupLeaveOf = (params: {
  costs: Invoice[];
  settlements: Settlement[];
  unitId: number;
  groupId: number;
}): IGroupLeave => {
  const { costs, settlements, unitId, groupId } = params;
  const groupCosts = openCosts({ costs, settlements }).filter(
    cost => cost.unitId === String(groupId),
  );
  const totalCents = groupCosts.reduce(
    (sum, cost) =>
      sum +
      toCents(cost.amountExclVat) +
      toCents(
        vatOfAmount({
          amountExclVat: cost.amountExclVat,
          vatPct: vatPercent(cost.vatPct),
        }),
      ),
    0,
  );
  return {
    namedIn: settlements.filter(settlement =>
      namesUnit(settlement, String(unitId)),
    ),
    openCosts: groupCosts.length,
    openTotal: fromCents(totalCents),
  };
};

// The building's costs and settlements, its units' own included (a
// departure on the unit counted the building's costs too).
export const fetchGroupLeave = async (params: {
  unitId: number;
  groupId: number;
}): Promise<IGroupLeave> => {
  const { unitId, groupId } = params;
  const filter = propertyFilter([unitId, groupId]);
  const [costs, settlements] = await Promise.all([
    fetchEveryPage(async page => {
      const response = await propertyCostApi.getPaged({
        query: { page, limit: PAGE_LIMIT, filter },
      });
      const { items, meta } = response.data.data;
      return {
        items: items.map(propertyCostToInvoice),
        totalPages: meta.totalPages,
      };
    }),
    fetchEveryPage(async page => {
      const response = await settlementApi.getPaged({
        query: { page, limit: PAGE_LIMIT, filter },
      });
      const { items, meta } = response.data.data;
      return {
        items: items.map(settlementFromApi),
        totalPages: meta.totalPages,
      };
    }),
  ]);
  return groupLeaveOf({ costs, settlements, unitId, groupId });
};
